import {Box, Button, Typography, alpha, useTheme} from '@mui/material';
import React from 'react';

type EmptyStateProps = {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'text' | 'outlined' | 'contained';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  size?: 'small' | 'medium' | 'large';
};

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'medium',
}) => {
  const theme = useTheme();

  const sizeConfig = {
    small: {
      iconSize: 48,
      iconContainerSize: 80,
      titleVariant: 'h6' as const,
      descVariant: 'body2' as const,
      padding: 3,
      gap: 1.5,
    },
    medium: {
      iconSize: 64,
      iconContainerSize: 100,
      titleVariant: 'h5' as const,
      descVariant: 'body1' as const,
      padding: 4,
      gap: 2,
    },
    large: {
      iconSize: 80,
      iconContainerSize: 120,
      titleVariant: 'h4' as const,
      descVariant: 'body1' as const,
      padding: 6,
      gap: 2.5,
    },
  };

  const config = sizeConfig[size];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: config.padding,
        px: 2,
        gap: config.gap,
      }}>
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: config.iconContainerSize,
            height: config.iconContainerSize,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            '& svg': {
              fontSize: config.iconSize,
            },
          }}>
          {icon}
        </Box>
      )}
      <Box sx={{maxWidth: 400}}>
        <Typography
          variant={config.titleVariant}
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 600,
          }}>
          {title}
        </Typography>
        {description && (
          <Typography
            variant={config.descVariant}
            sx={{
              color: 'text.secondary',
            }}>
            {description}
          </Typography>
        )}
      </Box>
      {(action || secondaryAction) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mt: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
          {action && (
            <Button
              variant={action.variant || 'contained'}
              onClick={action.onClick}
              color="primary">
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="text" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
